import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  DigestEvent,
  DigestPin,
  DigestRun,
  DigestRunStageId,
  StartDigestAnalysisResponse,
} from '@ozon-ai/analysis/contracts';
import type { ProductCatalogItem } from '@ozon-ai/ozon/contracts';
import {
  DIGEST_EVENT_SEVERITY_LABELS,
  DIGEST_OVERALL_STATUS_LABELS,
  DIGEST_RUN_STAGES,
  DIGEST_RUN_STATUS_LABELS,
} from '@ozon-ai/analysis/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../api/client';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ContentCard } from '../../ui/ContentCard';
import { ErrorText, HintText, PageShell, SectionTitle } from '../../ui/PageShell';
import { StatusBadge } from '../../ui/StatusBadge';

const EVENT_GROUPS: Array<{ severity: DigestEvent['severity']; title: string }> = [
  { severity: 'critical', title: 'Критичные' },
  { severity: 'warning', title: 'Предупреждения' },
  { severity: 'info', title: 'Информация' },
  { severity: 'positive', title: 'Положительные изменения' },
];

function stageLabel(id: string): string {
  return DIGEST_RUN_STAGES.find((stage) => stage.id === (id as DigestRunStageId))?.label ?? id;
}

function formatRub(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`;
}

function productTitle(
  offerId: string,
  productName: string | undefined,
  names: Map<string, string>,
): string {
  const fromCatalog = names.get(offerId);
  if (fromCatalog) return fromCatalog;
  const stored = productName?.trim();
  if (stored && stored !== offerId) return stored;
  return offerId;
}

function presentNarrative(text: string, names: Map<string, string>): string {
  let next = text.replace(/Босс,\s*/g, '').replace(/Босс/g, '');
  const ids = [...names.keys()].sort((left, right) => right.length - left.length);
  for (const offerId of ids) {
    const title = names.get(offerId);
    if (!title || title === offerId) continue;
    next = next.split(offerId).join(title);
  }
  return next.replace(/[ \t]{2,}/g, ' ').trim();
}

async function loadRun(id?: string): Promise<DigestRun | null> {
  try {
    const query = id ? `?id=${encodeURIComponent(id)}` : '';
    return await api.get<DigestRun>(`/digest-analyses${query}`);
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404) return null;
    throw err;
  }
}

export function DigestPage() {
  const navigate = useNavigate();
  const [run, setRun] = useState<DigestRun | null>(null);
  const [pins, setPins] = useState<DigestPin[]>([]);
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);

  const refresh = useCallback(async (id?: string) => {
    const next = await loadRun(id);
    setRun(next);
    return next;
  }, []);

  const refreshPins = useCallback(async () => {
    const next = await api.get<DigestPin[]>('/digest-pins');
    setPins(next);
  }, []);

  useEffect(() => {
    void Promise.all([
      refresh(),
      refreshPins(),
      api
        .get<ProductCatalogItem[]>('/products/catalog')
        .then(setCatalog)
        .catch(() => setCatalog([])),
    ])
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Не удалось загрузить дайджест'),
      )
      .finally(() => setLoading(false));
  }, [refresh, refreshPins]);

  const running = run?.status === 'running' || run?.status === 'created';

  useEffect(() => {
    if (!running || !run) return;
    const timer = window.setTimeout(() => {
      void refresh(run.id).catch((err) =>
        setError(err instanceof Error ? err.message : 'Не удалось обновить дайджест'),
      );
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [running, run, refresh]);

  async function handleRun() {
    setBusy(true);
    setError(null);
    try {
      const started = await api.post<StartDigestAnalysisResponse>('/digest-analyses/run', {});
      await refresh(started.runId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить дайджест');
    } finally {
      setBusy(false);
    }
  }

  async function togglePin(offerId: string, pinned: boolean) {
    setError(null);
    try {
      if (pinned) {
        await api.delete(`/digest-pins?offerId=${encodeURIComponent(offerId)}`);
      } else {
        await api.post('/digest-pins', { offerId });
      }
      await refreshPins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить закрепление');
    }
  }

  const pinnedIds = useMemo(() => new Set(pins.map((item) => item.offerId)), [pins]);
  const namesByOfferId = useMemo(() => {
    const names = new Map<string, string>();
    for (const item of catalog) {
      const title = item.name?.trim();
      if (title && title !== item.offerId) names.set(item.offerId, title);
    }
    return names;
  }, [catalog]);
  const result = run?.result;
  const groups = useMemo(
    () =>
      EVENT_GROUPS.map((group) => ({
        ...group,
        events: result?.events.filter((event) => event.severity === group.severity) ?? [],
      })).filter((group) => group.events.length > 0),
    [result],
  );

  if (loading) return <LoadingSpinner />;

  const currentStages = run?.progress.currentStages.map(stageLabel).join(', ');

  return (
    <PageShell
      title="Дайджест"
      action={
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/analyses')}>
          Назад
        </Button>
      }
    >
      <HintText>
        Короткий обзор FBO: что изменилось к прошлому периоду и что проверить. Детальный расчёт
        остаётся в анализе SKU.
      </HintText>
      {error ? <ErrorText>{error}</ErrorText> : null}

      <ContentCard>
        <div className="flex flex-col gap-3">
          <Button type="button" disabled={busy || running} onClick={() => void handleRun()}>
            {busy || running ? 'Считаем…' : 'Запустить дайджест'}
          </Button>
          {!run ? <p className="text-sm text-muted-foreground">Запусков ещё не было.</p> : null}
          {run ? (
            <p className="text-sm text-muted-foreground">
              {DIGEST_RUN_STATUS_LABELS[run.status] ?? run.status}
              {run.periodFrom ? ` · ${run.periodFrom} — ${run.periodTo}` : ''}
            </p>
          ) : null}
          {running ? <p className="text-sm">Сейчас: {currentStages || 'подготовка'}</p> : null}
          {run?.status === 'failed' && run.error ? <ErrorText>{run.error}</ErrorText> : null}
        </div>
      </ContentCard>

      {result ? (
        <ContentCard>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={result.overallStatus}>
                {DIGEST_OVERALL_STATUS_LABELS[result.overallStatus] ?? result.overallStatus}
              </StatusBadge>
              <p className="text-sm text-muted-foreground">
                {result.period.from} — {result.period.to}
              </p>
            </div>
            <p className="text-sm">
              Критично {result.counts.critical} · внимание {result.counts.warning} · положительные{' '}
              {result.counts.positive}
            </p>
            <p className="text-sm text-muted-foreground">
              Проанализировано {result.counts.analyzed}, исключено {result.counts.excluded}, с
              ошибкой {result.counts.failed}
            </p>
            {run?.narrative?.text ? (
              <p className="text-sm">{presentNarrative(run.narrative.text, namesByOfferId)}</p>
            ) : null}
            {result.recommendations[0] ? (
              <p className="text-sm">Дальше: {result.recommendations[0].actionHint}</p>
            ) : null}
            {result.limitations.map((item) => (
              <p key={item} className="text-sm text-muted-foreground">
                {item}
              </p>
            ))}
            {result.failedOfferIds.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                Не обработаны:{' '}
                {result.failedOfferIds
                  .map((offerId) => productTitle(offerId, undefined, namesByOfferId))
                  .join(', ')}
              </p>
            ) : null}
          </div>
        </ContentCard>
      ) : null}

      {groups.map((group) => (
        <div key={group.severity}>
          <SectionTitle>{group.title}</SectionTitle>
          {group.events.map((event) => (
            <EventCard
              key={event.key}
              title={productTitle(event.offerId, event.productName, namesByOfferId)}
              event={event}
              open={expandedKey === event.key}
              pinned={pinnedIds.has(event.offerId)}
              snapshotRunId={
                result?.snapshots.find((item) => item.offerId === event.offerId)?.runId
              }
              onToggle={() => setExpandedKey(expandedKey === event.key ? null : event.key)}
              onOpenAnalysis={(runId) =>
                navigate(
                  `/analyses/sku/${encodeURIComponent(event.offerId)}/runs/${encodeURIComponent(runId)}`,
                )
              }
              onTogglePin={() => void togglePin(event.offerId, pinnedIds.has(event.offerId))}
            />
          ))}
        </div>
      ))}

      {run?.scope ? (
        <div>
          <SectionTitle>Состав выборки</SectionTitle>
          <ContentCard>
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                В наблюдении {run.scope.includedCount}, исключено {run.scope.excludedCount}. Окно{' '}
                {run.scope.observationFrom} — {run.scope.observationTo}.
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={() => setScopeOpen((value) => !value)}>
                {scopeOpen ? 'Скрыть причины' : 'Показать причины'}
              </Button>
              {scopeOpen
                ? run.scope.items.map((item) => (
                    <p key={item.offerId} className="text-sm text-muted-foreground">
                      {productTitle(item.offerId, item.productName, namesByOfferId)}:{' '}
                      {item.included ? 'включён' : 'исключён'} —{' '}
                      {item.reasons.join(', ')}
                    </p>
                  ))
                : null}
            </div>
          </ContentCard>
        </div>
      ) : null}
    </PageShell>
  );
}

function EventCard({
  title,
  event,
  open,
  pinned,
  snapshotRunId,
  onToggle,
  onOpenAnalysis,
  onTogglePin,
}: {
  title: string;
  event: DigestEvent;
  open: boolean;
  pinned: boolean;
  snapshotRunId?: string;
  onToggle: () => void;
  onOpenAnalysis: (runId: string) => void;
  onTogglePin: () => void;
}) {
  return (
    <ContentCard>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className="flex-1 font-semibold">{title}</p>
          <StatusBadge status={event.severity}>
            {DIGEST_EVENT_SEVERITY_LABELS[event.severity] ?? event.severity}
          </StatusBadge>
        </div>
        <p className="text-sm">{event.title}</p>
        {event.moneyImpactRub != null ? (
          <p className="text-sm">Эффект: {formatRub(event.moneyImpactRub)}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onToggle}>
            {open ? 'Скрыть' : 'Подробнее'}
          </Button>
          {snapshotRunId ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenAnalysis(snapshotRunId)}>
              Анализ SKU
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={onTogglePin}>
            {pinned ? 'Снять закрепление' : 'Закрепить'}
          </Button>
        </div>
        {open ? (
          <div className="flex flex-col gap-1">
            <p className="text-sm text-muted-foreground">{event.message}</p>
            {event.evidence.map((item) => (
              <p key={`${item.path}-${String(item.value)}`} className="text-sm text-muted-foreground">
                {item.label}: {String(item.value)}
              </p>
            ))}
            {event.actionHint ? <p className="text-sm">Действие: {event.actionHint}</p> : null}
          </div>
        ) : null}
      </div>
    </ContentCard>
  );
}
